import { AuthenticationUserLoggedIn } from '@bett3r-dev/teselly-domain';
import { Capabilities, type CapabilityKey, type ScopeLevel } from '@bett3r-dev/teselly-capabilities';
import { setExecutionContextProvider } from '@bett3r-dev/teselly-client-library';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import * as Sentry from '@sentry/react';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isLiveAccountChangeFrame } from '@infrastructure/realtime/accountChangeFrame';
import { createAccountSuspensionSignal } from '@infrastructure/suspension/accountSuspensionSignal';
import { TOURS_STORAGE_KEY } from '@contexts/useTour/useTour';
import { ProvisioningOverlay } from './ProvisioningOverlay';

// TV1-1860 — admin impersonation. When an admin opens the web app via the
// admin panel's "Impersonate" action, the tab loads with `?impersonate=<token>`.
// We exchange the token for a BA session cookie and persist the impersonated
// user + admin's BA user ID in sessionStorage so a same-tab refresh keeps the
// banner state alive without another round-trip.
export type ImpersonatedUser = { id: string; email: string; name: string };

const IMPERSONATION_STORAGE_KEY = 'teselly.impersonation';

// TV1-1782 ticket 11 — the /users-profiles/me payload now carries the
// capability-tier projection (capabilities + scopesByCapability) plus the
// per-request assigned-scopes list. We surface them on the User type so
// downstream UI guards (capability gates, scope filters) can read them
// directly from the auth context with no extra fetch. Defaults are empty
// arrays/objects until the subscription hydrates.
export type User = AuthenticationUserLoggedIn & {
  capabilities: CapabilityKey[];
  scopesByCapability: Partial<Record<CapabilityKey, ScopeLevel[]>>;
  assignedScopes: string[];
  // TV1-1783 — billing-account suspension state, carried on `/users-profiles/me`.
  // TV1-2077 — drives the informational SuspensionBanner (the v1 block screen is
  // retired); set ONLY from `/me`. Defaults to `'active'`.
  suspensionStatus: 'active' | 'suspended';
};

export type AuthContextType = {
  user: User | null;
  isLoadingUser: boolean;
  // TV1-1879 — true once the query-first capability hydration has settled
  // ( either the HTTP query resolved or it failed and we fell back to the
  // subscription ). Capability gates ( GatedRoute, ProtectedHomeRedirect )
  // wait on this so they never decide against an un-hydrated `{}` scope map.
  capabilitiesReady: boolean;
  isProvisioning: boolean;
  provisioningError: string | null;
  retryProvisioning: () => void;
  signin: ( email: string ) => Promise<void>;
  signinWithPassword: ( email: string, password: string ) => Promise<User | null>;
  loginWithGoogle: () => Promise<void>;
  loginWithMercadoLibre: () => Promise<void>;
  register: ( email: string, name: string, authStrategy?: 'magiclink' | 'password' ) => Promise<User | null>;
  signout: () => Promise<void>;
  validateMagicLink: ( token: string ) => Promise<User | null>;
  requestPasswordReset: ( email: string ) => Promise<void>;
  resetPassword: ( token: string, newPassword: string ) => Promise<void>;
  // TV1-1860 — admin impersonation state. `isImpersonating` reflects whether
  // the current tab is operating under an admin-issued impersonation session.
  // `impersonatedByAdminId` is the admin's BA user ID, exposed so the client
  // library can forward it as `x-auth-impersonated-by` for audit propagation.
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  impersonatedByAdminId: string | null;
  stopImpersonating: () => Promise<void>;
};

// 30s budget for the async account-provisioning chain to complete after a
// first-time login. In a healthy environment this is sub-second; the timeout
// exists so the user sees an error instead of a stuck spinner if something
// is truly wrong.
const PROVISIONING_TIMEOUT_MS = 30_000;

const authContext = createContext<AuthContextType | undefined>( undefined );

export function ProvideAuth({ children }: { children: React.ReactNode }) {
  const _auth = useProvideAuth();
  return (
    <authContext.Provider value={_auth}>
      {children}
      <ProvisioningOverlay />
    </authContext.Provider>
  );
}

export default function useAuth() {
  return useContext( authContext );
}

function useProvideAuth() {
  const { backend, realTimeSession, setRealTimeSession } = useBackend() as BackendContextType;
  const [ user, setUser ] = useState<User | null>( null );
  // Capabilities are a SEPARATE concern from the profile: they come from the
  // `/identity/my-capabilities` readmodel, NOT `/users-profiles/me`. Keeping them
  // in their own state ( rather than as fields on the `user` object ) means a
  // profile update can never clobber the capability map and vice-versa. The
  // exposed `user` ( `effectiveUser` below ) grafts the two together for consumers.
  const [ scopesByCapability, setScopesByCapability ] = useState<Partial<Record<CapabilityKey, ScopeLevel[]>>>({});
  const [ isLoadingUser, setIsUserLoading ] = useState<boolean>( true );
  const [ capabilitiesReady, setCapabilitiesReady ] = useState<boolean>( false );
  const [ isProvisioning, setIsProvisioning ] = useState<boolean>( false );
  const [ provisioningError, setProvisioningError ] = useState<string | null>( null );

  // TV1-1860 — admin impersonation state. These live alongside the regular
  // auth state so the banner, audit-metadata header, and stop-impersonating
  // command all stay in lock-step. State is mirrored to sessionStorage so a
  // same-tab refresh restores the banner without another token exchange.
  const [ isImpersonating, setIsImpersonating ] = useState<boolean>( false );
  const [ impersonatedUser, setImpersonatedUser ] = useState<ImpersonatedUser | null>( null );
  const [ impersonatedByAdminId, setImpersonatedByAdminId ] = useState<string | null>( null );

  // Subscription to /users-profiles/me — reused for the initial fetch + the
  // first-time provisioning wait. Closed on signout / unmount.
  const profileSubscriptionRef = useRef<{ close: () => void } | null>( null );
  const provisioningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null );
  const provisioningPollRef = useRef<ReturnType<typeof setInterval> | null>( null );

  // Capabilities are loaded QUERY-FIRST and refreshed via a SIGNAL-mode
  // subscription: the identity projector pings this user's `/identity/my-capabilities`
  // subscription whenever their role assignment changes or a role's grants are
  // edited, and we re-run the ( correctly user-scoped ) HTTP query to refresh the
  // gates — we never consume the raw realtime row ( see `refetchCapabilities` ).
  // Closed on signout / unmount alongside the profile subscription.
  const capabilitiesSubscriptionRef = useRef<{ close: () => void } | null>( null );
  // TV1-2071 — `suspensionStatus` is the one field on /me that no /me push can
  // deliver: it is computed inside the `/me` queryHandler by cross-reading the
  // accounts readmodel, and the /me subscription fires only on UserProfile*
  // events, which `ClientAccountResumed` does not touch. So the account row is
  // watched directly and /me is re-READ on its signal. Closed alongside the other
  // two subscriptions. Full rationale in `accountSuspensionSignal.ts`.
  const accountSuspensionSignalRef = useRef<{ close: () => void } | null>( null );
  // Debounce coalescing for capability signals ( a burst of pings collapses to a
  // single refetch ).
  const capabilitiesSignalDebounceRef = useRef<ReturnType<typeof setTimeout> | null>( null );

  // TV1-1860 — keep the latest user + impersonatedByAdminId addressable by
  // the module-level `setExecutionContextProvider` callback. The callback is
  // installed once on mount but reads the live values via refs to avoid
  // re-registering the provider on every state change.
  const userRef = useRef<User | null>( null );
  userRef.current = user;
  const impersonatedByAdminIdRef = useRef<string | null>( null );
  impersonatedByAdminIdRef.current = impersonatedByAdminId;
  // TV1-2103 — keep the live realtime session addressable by
  // `subscribeToCapabilities` (called from the async hydrate path) without
  // re-registering on every render. Mirrored here the same way as `userRef`.
  const realtimeSessionRef = useRef( realTimeSession );
  realtimeSessionRef.current = realTimeSession;

  // Derived capability list + the merged user surfaced to consumers. `can()` and
  // the `useCan`/`useCapability` hooks read `user.scopesByCapability`, so this is
  // the single, read-only seam where the profile and capability concerns meet —
  // neither write path knows about the other.
  const capabilities = useMemo(
    () => Object.keys( scopesByCapability ) as CapabilityKey[],
    [scopesByCapability]
  );
  const effectiveUser = useMemo<User | null>(
    () => ( user ? { ...user, capabilities, scopesByCapability } : null ),
    [ user, capabilities, scopesByCapability ]
  );

  // TV1-1782 ticket 11 — normalize the /users-profiles/me payload so that
  // capabilities / scopesByCapability / assignedScopes always have safe
  // defaults when the subscription has not yet hydrated them.
  const normalizeUser = ( raw: ( Partial<User> & Record<string, unknown> ) | undefined ): User => ({
    ...raw,
    capabilities: raw?.capabilities ?? [],
    scopesByCapability: raw?.scopesByCapability ?? {},
    assignedScopes: raw?.assignedScopes ?? [],
    // TV1-1783 — default to active until /me hydrates the real status.
    suspensionStatus: raw?.suspensionStatus ?? 'active'
  } as User );

  // I7 ( SDD §6.3 ) helper — `systemOwned: true` capabilities must never reach
  // the client. Used to filter the raw capability-projection row before it
  // hydrates the auth context ( the gateway's X-Auth-Capabilities header filters
  // the same way at its boundary ). Renamed from `internal` by TV1-1535: the
  // flag's exposure meaning was a duplicate of the route's `visibility` and was
  // deleted; what remains is grantability + disclosure.
  const isInternalCapability = ( key: string ): boolean =>
    ( Capabilities as Record<string, { systemOwned?: boolean }> )[ key ]?.systemOwned === true;

  // TV1-1860 — local impersonation state mutators. Centralised so every
  // entry/exit point (token exchange success/failure, signout, stop, 401
  // handler) clears sessionStorage and React state in lock-step.
  const clearImpersonationState = () => {
    try {
      sessionStorage.removeItem( IMPERSONATION_STORAGE_KEY );
    } catch {
      // sessionStorage may throw under private-browsing restrictions —
      // ignore; React state below remains authoritative.
    }
    setIsImpersonating( false );
    setImpersonatedUser( null );
    setImpersonatedByAdminId( null );
  };

  // TV1-1860 — install a module-level ExecutionContextProvider so the typed
  // client library forwards `accountId`, `userId`, and `x-auth-impersonated-by`
  // on every request. The cookie-auth path in the middleware wins over these
  // headers in normal operation, so this is primarily belt-and-suspenders for
  // the gateway-headers path; the impersonation header is the one that earns
  // its keep — it makes the admin id reachable when only x-auth-* propagation
  // is active. Refs are mutated in render (below) so this callback always
  // observes the latest values without re-registering on every state change.
  useEffect(() => {
    setExecutionContextProvider(() => {
      const currentUser = userRef.current;
      const adminId = impersonatedByAdminIdRef.current;
      // Returning `undefined` short-circuits the x-auth-* header injection
      // in the client library (see SSE.ts:264) — desirable when we have
      // nothing identity-shaped to forward yet.
      if ( !currentUser && !adminId ) return undefined;
      return {
        ...( currentUser?.accountId ? { accountId: currentUser.accountId } : {}),
        ...( currentUser?.id ? { userId: currentUser.id } : {}),
        ...( adminId ? { impersonatedBy: adminId } : {})
      };
    });
  }, []);

  useEffect(() => {
    if ( backend && user?.accountId ) {
      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const wsRoute = backendUrl.startsWith( '/' )
        ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${backendUrl}`
        : backendUrl.replace( 'https', 'wss' ).replace( 'http', 'ws' );
      const realtimeSession = backend.initializeRealtimeSession( `${wsRoute}/realtime-session` );
      setRealTimeSession( realtimeSession );
    }
  }, [ backend, setRealTimeSession, user?.accountId ]);

  const clearProvisioningTimeout = () => {
    if ( provisioningTimeoutRef.current ) {
      clearTimeout( provisioningTimeoutRef.current );
      provisioningTimeoutRef.current = null;
    }
  };

  const clearProvisioningPoll = () => {
    if ( provisioningPollRef.current ) {
      clearInterval( provisioningPollRef.current );
      provisioningPollRef.current = null;
    }
  };

  const startProvisioningTimeout = () => {
    clearProvisioningTimeout();
    provisioningTimeoutRef.current = setTimeout(() => {
      Sentry.addBreadcrumb({ category: 'auth.race', level: 'error', message: 'provisioning timeout', data: { branch: 'timeout' }});
      setProvisioningError( 'PROVISIONING_TIMEOUT' );
      setIsProvisioning( false );
      setIsUserLoading( false );
      clearProvisioningPoll();
    }, PROVISIONING_TIMEOUT_MS );
  };

  // Backstop for the realtime subscription: the WebSocket handshake captures
  // the user's accountId at connection time (empty during first-time login),
  // so server-side scope filtering may drop the post-provisioning push. A
  // plain query goes through the gateway, which re-reads the now-populated
  // Redis token cache and returns the populated profile.
  const startProvisioningPoll = () => {
    clearProvisioningPoll();
    provisioningPollRef.current = setInterval( async () => {
      try {
        const profile = await backend.queries.UsersProfilesMe({});
        if ( profile?.accountId ) {
          setUser( normalizeUser( profile as Partial<User> & Record<string, unknown> ));
          clearProvisioningTimeout();
          clearProvisioningPoll();
          setIsProvisioning( false );
          setIsUserLoading( false );
          setProvisioningError( null );
          subscribeToProfile();
          void hydrateCapabilities();
        }
      } catch {
        // transient — keep polling until timeout
      }
    }, 1_000 );
  };

  const onProfileChange = ( change: any ) => {
    // Subscription emits both snapshots (initial state) and per-event deltas.
    // We always use the latest payload — accountId populated == provisioned.
    const fresh: User | undefined = change?.type === 'snapshot'
      ? change?.data?.items?.[0] ?? change?.data
      : change?.data;
    if ( !fresh ) return;
    // Profile updates touch ONLY profile-owned fields. Capabilities live in their
    // own state ( `scopesByCapability` ) and are neither read nor written here —
    // the profile concern knows nothing about the capability concern.
    setUser( normalizeUser( fresh ));
    if ( fresh.accountId ) {
      clearProvisioningTimeout();
      clearProvisioningPoll();
      setIsProvisioning( false );
      setProvisioningError( null );
    }
  };

  const subscribeToProfile = () => {
    if ( profileSubscriptionRef.current ) return;
    profileSubscriptionRef.current = backend.subscriptions.UsersProfilesMe(
      {} as any,
      onProfileChange
    );
  };

  // Apply the rows returned by the user-scoped `/identity/my-capabilities` query
  // to the auth context. `assignedScopes` is untouched ( it rides on /me from the
  // request context, not on this projection ).
  const applyCapabilitiesRows = ( rows: any[]) => {
    // Defence-in-depth ( defect-3 guard ): only ever apply THIS user's own row.
    // The HTTP query is already user-scoped, but the realtime push path has
    // historically not re-applied the per-user filter, so a stray row for
    // another account member must never clobber our capability map.
    const myId = userRef.current?.id;
    const row = myId ? rows.find(( r ) => r?.userId === myId ) : rows[0];
    // If no row matches this user ( empty/transient result, or a stray row for
    // another member ), leave the current map untouched — never wipe caps on an
    // ambiguous response. Membership removal is reflected on the next full load.
    if ( !row?.scopesByCapability ) return;
    setScopesByCapability(
      Object.fromEntries(
        Object.entries( row.scopesByCapability as Record<string, ScopeLevel[]> )
          .filter(([key]) => !isInternalCapability( key ))
      ) as Record<CapabilityKey, ScopeLevel[]>
    );
  };

  // Re-run the ( user-scoped, correctly-filtered ) HTTP query and apply the
  // result. This is the authoritative source for live capability refreshes —
  // we deliberately do NOT consume realtime delta rows ( the push path can carry
  // another member's row; the query cannot ).
  const refetchCapabilities = async () => {
    try {
      const rows = await backend.queries.IdentityMyCapabilities({});
      applyCapabilitiesRows( Array.isArray( rows ) ? rows : [rows]);
    } catch {
      // Transient gateway/network — keep the current capability set.
    }
  };

  const subscribeToCapabilities = () => {
    if ( capabilitiesSubscriptionRef.current ) return;
    // TV1-2103 — the `/identity/my-capabilities` readmodel subscription was
    // retired when the route moved to the live gateway resolver. Live capability
    // refreshes now arrive on a raw realtime-session `'capabilities'` channel
    // ( single handler, no router abstraction ). We subscribe with the wildcard
    // ref `'*'` so BOTH server-side pushes are delivered: the per-user push
    // ( `send(userId, { ..., ref: userId })` on a role/grant change ) AND the
    // ref-less best-effort boot `broadcast` ( catalog-change deploy freshness,
    // where no event fires ). The client dispatcher fans every message to both
    // its `${type}-${ref}` and `${type}-*` buckets, and the server already
    // socket-scopes `send(userId)` to this user — so `'*'` here receives only
    // what is addressed to this client plus all-user broadcasts, needing no
    // userId filter. ( Subscribing by `userId` alone silently dropped the
    // ref-less boot broadcast — it never matches `capabilities-${userId}`. ) On
    // a ping we refetch via the ( correctly user-scoped ) HTTP query rather than
    // trusting a pushed row, debounced to coalesce a burst into one refetch.
    const realtimeSession = realtimeSessionRef.current;
    const userId = userRef.current?.id;
    if ( !realtimeSession || !userId ) return;
    const onCapabilitiesSignal = () => {
      if ( capabilitiesSignalDebounceRef.current ) clearTimeout( capabilitiesSignalDebounceRef.current );
      capabilitiesSignalDebounceRef.current = setTimeout(() => {
        capabilitiesSignalDebounceRef.current = null;
        void refetchCapabilities();
      }, 200 );
    };
    realtimeSession.onMessage( 'capabilities', '*', onCapabilitiesSignal );
    capabilitiesSubscriptionRef.current = {
      close: () => realtimeSession.offMessage( 'capabilities', '*', onCapabilitiesSignal )
    };
  };

  // TV1-2071 — the live half of AC-2 for the layout suspension banner. The
  // account row is the only channel that carries a suspension flip, so we watch it
  // in SIGNAL mode and re-read `/users-profiles/me` on a debounced signal, which
  // keeps /me the single source of truth for the field rather than trusting a
  // pushed row. Only `suspensionStatus` is patched, and only when it actually
  // moved: replacing the user object would clobber the capability map that is
  // deliberately a separate concern here, and a transient null user would unmount
  // and remount the banner.
  const subscribeToAccountSuspension = () => {
    if ( accountSuspensionSignalRef.current ) return;
    accountSuspensionSignalRef.current = createAccountSuspensionSignal({
      subscribe: ( onSignal ) => backend.subscriptions.AccountsReadmodel(
        { _snapshot: false, _changes: 'signal' } as any,
        ( change: any ) => {
          // ONE shared rule with `subscriptionDataPolicy.ts`, which reads the SAME
          // channel for the other half of AC-2. See
          // `@infrastructure/realtime/accountChangeFrame` for why "anything but the
          // snapshot" and not a strict `type === 'signal'`: the two halves diverging
          // is a failure that looks exactly like "nothing changed".
          if ( isLiveAccountChangeFrame( change )) onSignal();
        }
      ),
      fetchSuspensionStatus: async () => {
        const profile = await backend.queries.UsersProfilesMe({}) as { suspensionStatus?: 'active' | 'suspended' } | undefined;
        return profile?.suspensionStatus;
      },
      onStatus: ( suspensionStatus ) => {
        setUser(( previous ) => (
          !previous || previous.suspensionStatus === suspensionStatus
            ? previous
            : { ...previous, suspensionStatus }
        ));
      }
    });
  };

  const closeAccountSuspensionSignal = () => {
    accountSuspensionSignalRef.current?.close();
    accountSuspensionSignalRef.current = null;
  };

  const closeCapabilitiesSubscription = () => {
    if ( capabilitiesSignalDebounceRef.current ) {
      clearTimeout( capabilitiesSignalDebounceRef.current );
      capabilitiesSignalDebounceRef.current = null;
    }
    capabilitiesSubscriptionRef.current?.close();
    capabilitiesSubscriptionRef.current = null;
  };

  // TV1-1879 — capabilities are loaded QUERY-FIRST, then subscribed for live
  // deltas, mirroring the profile path and the feature-flag provider's
  // documented "query-first, then subscribe" pattern. The WS snapshot is
  // unreliable on initial connect: the realtime handshake captures an empty
  // accountId during first-time login, so the server's FilterByAccountId /
  // FilterByUserId transformers can drop the snapshot push. When that happened
  // `scopesByCapability` stayed `{}` permanently — every capability gate then
  // silently hid its control with no corrective re-render ( only a hard page
  // refresh fixed it ). The HTTP query reliably carries the session cookie, so
  // we hydrate from it first and only then open the subscription for live
  // updates. `capabilitiesReady` flips true once the query settles ( success OR
  // failure ) so the gates never hang on a loader; on query failure the
  // subscription remains the fallback source.
  const hydrateCapabilities = async () => {
    setCapabilitiesReady( false );
    try {
      const rows = await backend.queries.IdentityMyCapabilities({});
      applyCapabilitiesRows( Array.isArray( rows ) ? rows : [rows]);
    } catch {
      // Query failed ( transient gateway/network ) — leave caps at their
      // current value; the subscription opened below is the fallback source.
    } finally {
      setCapabilitiesReady( true );
      subscribeToCapabilities();
      subscribeToAccountSuspension();
    }
  };

  const beginProvisioningWatch = () => {
    setIsProvisioning( true );
    setProvisioningError( null );
    startProvisioningPoll();
    startProvisioningTimeout();
  };

  const signin = async ( email: string ) => {
    // TV1-1633 ticket 17: `SendMagicLink` was demoted from a command to a
    // plain HTTP endpoint (`POST /identity/magic-link/send`). The typed
    // client exposes plain POST endpoints under `backend.post.<name>` where
    // `<name>` is the endpoint's `name` field snake_cased.
    // TODO(T32 regen verification): confirm this typed-client method name
    // (`send_magic_link`) once the client library is regenerated.
    await backend.post.send_magic_link({ email });
  };

  // `capabilitiesReady` (gates the whole protected route tree — see
  // `ProtectedHomeRedirect` / `GatedRoute`) is set ONLY inside
  // `hydrateCapabilities()`'s `finally`, and `hydrateCapabilities()` is only
  // otherwise invoked from the mount-time session-check effect's success
  // branches. A login triggered from an already-mounted SPA session (no full
  // page reload — the normal case for `signinWithPassword`/`validateMagicLink`)
  // never runs that effect again, so without this call `capabilitiesReady`
  // stays permanently `false` and `ProtectedHomeRedirect` hangs on its loader
  // forever, even though the session is fully valid. Mirrors the mount
  // effect's own accountId-present/absent branching (`beginProvisioningWatch`
  // vs `subscribeToProfile` + `hydrateCapabilities`).
  const afterLogin = ( loggedInUser: User ): void => {
    if ( loggedInUser.accountId ) {
      subscribeToProfile();
      void hydrateCapabilities();
    } else {
      beginProvisioningWatch();
    }
  };

  const signinWithPassword = async ( email: string, password: string ): Promise<User | null> => {
    // TV1-1633 ticket 15: command renamed from `LoginAuthenticationUser` and
    // split — the email/password branch is now `LoginWithPassword` with a
    // flat body (no `type` discriminator).
    const response = await backend.commands.LoginWithPassword({ body: { email, password }});
    if ( response.events ) {
      const loggedInUser = normalizeUser( response.events[0].data );
      setUser( loggedInUser );
      afterLogin( loggedInUser );
      return loggedInUser;
    }
    return null;
  };

  // Better Auth stock OAuth start endpoints — the frontend asks the server
  // for the provider authorization URL via the typed client library and
  // then navigates the browser to it (TV1-1619 D1, critique C1).
  //
  // BA appends `?error=<code>` to `errorCallbackURL` on failure. The
  // dedicated /auth/error route renders the typed error UI (TV1-1619
  // ticket 19) and forwards TWO_FACTOR_REQUIRED to /auth/two-factor.
  //
  // The server now drives BA's `POST /sign-in/social` (or `/sign-in/oauth2`)
  // in-process and returns the absolute provider URL (e.g.
  // `https://accounts.google.com/o/oauth2/v2/auth?...`) — navigate there
  // directly. We keep the legacy relative-path branch as a defensive
  // fallback so an out-of-sync server build can't break the FE.
  const navigateToOAuthStart = ( redirectUrl: string ) => {
    if ( /^https?:\/\//i.test( redirectUrl )) {
      window.location.href = redirectUrl;
      return;
    }
    const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
    window.location.href = `${backendUrl}${redirectUrl}`;
  };

  const buildOAuthCallbackParams = () => ({
    callbackURL: `${window.location.origin}/dashboard`,
    errorCallbackURL: `${window.location.origin}/auth/error`
  });

  const loginWithGoogle = async () => {
    const { redirectUrl } = await backend.get.sign_in_with_google( buildOAuthCallbackParams());
    navigateToOAuthStart( redirectUrl );
  };

  const loginWithMercadoLibre = async () => {
    const { redirectUrl } = await backend.get.sign_in_with_mercadolibre( buildOAuthCallbackParams());
    navigateToOAuthStart( redirectUrl );
  };

  const validateMagicLink = async ( token: string ): Promise<User | null > => {
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- invalid/expired magic link returns null (caller treats as auth failure); not a user-facing command error (TV1-2010 waiver, not a rethrow).
    try{
      // TV1-1633 ticket 15: command renamed from `LoginAuthenticationUser` and
      // split — the magic-link branch is now `LoginWithMagicLink` with a flat
      // body containing only the token.
      const response = await backend.commands.LoginWithMagicLink({ body: { token }});
      if ( response.events ) {
        const user = normalizeUser( response.events[0].data );
        setUser( user );
        afterLogin( user );
        return user;
      }else{
        return null;
      }
    }catch ( err ){
      return null ;
    }
  };

  const register = async ( email: string, name: string, authStrategy?: 'magiclink' | 'password' ): Promise<null> => {
    // Pass the name through verbatim. Splitting it here and re-joining the first
    // two words was wrong twice over: a single-word name made `lastName`
    // `undefined`, which the template literal stringified into a REAL name of
    // "undefined" (that string then rode `AccountRegistered` ->
    // `UserAccountAdded` -> `UserProfileRegistered` into the profile readmodel
    // and Better Auth's `user.name`), and a three-word name silently lost its
    // last word. `RegisterAccount` already splits `fullName` correctly on the
    // server — first token is the given name, `.slice(1).join(' ') || ''` the
    // rest — so the client has no reason to pre-parse it at all.
    await backend.commands.RegisterAccount({ id: uuidv4(), body: { email, fullName: name.trim(), authStrategy }});
    return null;
  };

  const signout = async () => {
    profileSubscriptionRef.current?.close();
    profileSubscriptionRef.current = null;
    closeCapabilitiesSubscription();
    closeAccountSuspensionSignal();
    clearProvisioningTimeout();
    clearProvisioningPoll();
    /**
     * ⚠️ BEST-EFFORT, and the try/catch is the point.
     *
     * This was a bare `await`. If the command rejected — the BA session already
     * revoked, expired, signed out in another tab, or just a network blip — the
     * rejection propagated out of `signout()` and EVERY line below it was skipped:
     * the state was never cleared, the storage was never dropped, and the
     * navigation never happened. A failed logout request left the app fully signed
     * in with its stores intact, which is the worst possible outcome for the one
     * action whose entire job is to leave nothing behind.
     *
     * Nothing here can meaningfully fail: the server-side session teardown is a
     * courtesy, and the local teardown below is what the user actually experiences.
     * `stopImpersonating` (below) has always guarded the same class of call for
     * exactly this reason; signout was the one that did not.
     */
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- soft-fail by design: the BA session may already be gone, and local teardown below must run regardless.
    try {
      await backend.commands.LogoutAuthenticationUser({ body: {}});
    } catch ( error: any ) {
      /**
       * Soft-fail, but NEVER silent.
       *
       * The session cookie is HttpOnly — the browser cannot clear it, only the
       * server can. So if this call fails, the local teardown below signs the user
       * out *visually* while the session stays alive on the server, and the next
       * sign-in on this browser resolves the PREVIOUS session. That is the single
       * most dangerous outcome this function has, and swallowing it without a trace
       * is what let it go unnoticed.
       *
       * A 403 here specifically means the route's permission gate rejected the
       * caller (`identity:logout-authentication-user` →
       * `identity:auth:anonymous`), i.e. this user cannot end their own session —
       * a server-side authorization defect, not a network blip. Say so loudly.
       */
      const status = error?.status;
      console.error(
        '[AUTH] Logout command FAILED — the server session was NOT revoked and the '
        + 'session cookie (HttpOnly) could not be cleared. This browser is still '
        + 'authenticated; the next sign-in may resolve the previous session.',
        { status, message: error?.message }
      );
      Sentry.captureException( error, { tags: { area: 'auth.signout', status: String( status ?? 'unknown' ) }});
    }
    setUser( null );
    setScopesByCapability({});
    setCapabilitiesReady( false );
    setIsProvisioning( false );
    setProvisioningError( null );
    // TV1-1860 — drop any cached impersonation context on explicit signout
    // so the next sign-in cannot leak the previous admin's audit trail.
    clearImpersonationState();

    /**
     * Tours are stored in ONE localStorage map for the whole browser origin, with
     * no account in the key (`useTour.tsx`). Left behind, the next account signs in
     * with every tour already marked as seen and is never onboarded. Cleared here
     * until that map moves to the user profile, which is the real fix.
     *
     * `teselly:list-density` and `teselly:inventory-preferences` are deliberately
     * NOT cleared — those are device preferences in the same class as the colour
     * scheme, and wiping them would be a surprise, not a fix.
     */
    try { localStorage.removeItem( TOURS_STORAGE_KEY ); } catch { /* private-browsing */ }

    /**
     * ⚠️ A FULL-DOCUMENT navigation, and it is load-bearing — not a style choice.
     *
     * This was `window.history.pushState({}, '', '/auth' )`, which is a client-side
     * navigation: the document is never torn down. Every CDSE domain store is a
     * MODULE-LEVEL zustand singleton created once at import (`createStore.ts`) with
     * no reset API, so all of them — onboarding wizard, dashboard, navbar account,
     * integrations — survived signout intact and the NEXT account to sign in
     * inherited the previous one's state. Reported in the wild: a freshly registered
     * account opened onto the prior account's onboarding progress and dashboard
     * figures.
     *
     * Reloading is the only fix that cannot rot. A registry of per-store reset
     * functions would work until someone adds a store and forgets to register it,
     * or until state leaks somewhere that is not a CDSE store at all (a React
     * context, a module-level cache, a live subscription). Dropping the whole module
     * graph is exhaustive by construction and costs one page load on an action that
     * is already a full context switch.
     *
     * `replace`, not `assign`, so Back cannot return to an authenticated route —
     * and matching `stopImpersonating` below, which has always reloaded correctly.
     * Anything that must outlive signout has to be written BEFORE this line.
     */
    window.location.replace( '/auth' );
  };

  // TV1-1860 — stop the in-progress impersonation. The aggregate command is
  // best-effort (the BA session may already be revoked, in which case the
  // command rejects and we soft-fail), then we clear local state and try to
  // close the tab. `window.close()` only succeeds for script-opened tabs, so
  // we always queue the `/auth` redirect as a fallback for direct URL access.
  const stopImpersonating = async () => {
    // Guard against callers that fire the action without an active
    // impersonation — the banner already gates this, but the function is
    // public on AuthContextType so any future caller (debug console,
    // programmatic teardown) shouldn't be able to hit the aggregate with
    // an empty id.
    if ( !impersonatedUser ) return;
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- soft-fail by design: BA session may already be revoked; local cleanup below is what the user sees (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.StopImpersonatingAuthUser({ id: impersonatedUser.id, body: {}});
    } catch {
      // Soft-fail — BA may have already ended the session, or the admin's
      // network blipped. Local cleanup below is what the user actually sees.
    }
    // Clear sessionStorage synchronously BEFORE the navigation kicks the
    // tree down. We deliberately skip the React state setters in
    // clearImpersonationState() — the tab is about to close or navigate,
    // so the setState would land on an unmounted tree and the
    // ImpersonationBanner's setIsLoading(false) finally-block would warn.
    // The unmount tears state down for us; we only need to drop the
    // sessionStorage key so a future tab on this origin starts clean.
    try { sessionStorage.removeItem( IMPERSONATION_STORAGE_KEY ); } catch { /* private-browsing */ }
    // `window.close()` only takes effect for script-opened tabs and is a
    // silent no-op otherwise — always fall through to the redirect so
    // direct-URL impersonation lands somewhere sensible.
    window.close();
    window.location.replace( '/auth' );
  };

  const requestPasswordReset = async ( email: string ): Promise<void> => {
    // TV1-1633 ticket 17: `RequestPasswordReset` was demoted from a command
    // to a plain HTTP endpoint (`POST /identity/password/forget`). The typed
    // client exposes plain endpoints under `backend.post.*`. Confirm method
    // name shape post-T32 regen.
    await backend.post.forgot_password({ email });
  };

  const resetPassword = async ( token: string, newPassword: string ): Promise<void> => {
    // TV1-1633 (decision 17): demoted from a command to a plain endpoint. The
    // post-link-click reset flow is anonymous (only the BA-minted token, no
    // session, no scope) so an aggregate command was the wrong shape.
    await backend.post.reset_password({ token, newPassword });
  };

  const retryProvisioning = () => {
    if ( !user ) return;
    setCapabilitiesReady( false );
    profileSubscriptionRef.current?.close();
    profileSubscriptionRef.current = null;
    closeCapabilitiesSubscription();
    closeAccountSuspensionSignal();
    beginProvisioningWatch();
  };

  // Initial fetch + permanent subscription. The subscription handles both
  // returning users (snapshot lands immediately with accountId populated)
  // and first-time landings (subscription stays open until accountId
  // populates via the post-OAuth provisioning chain).
  useEffect(() => {
    if ( !backend ) return;
    const startTime = performance.now();

    // TV1-1860 — restore impersonation state from sessionStorage so a same-tab
    // refresh keeps the banner alive (the BA cookie also persists for the
    // session's 1h TTL). Parse failures wipe the corrupt key — local React
    // state remains authoritative if storage is unavailable.
    try {
      const stored = sessionStorage.getItem( IMPERSONATION_STORAGE_KEY );
      if ( stored ) {
        try {
          const parsed = JSON.parse( stored ) as { user: ImpersonatedUser; impersonatedByAdminId: string | null };
          if ( parsed?.user ) {
            setIsImpersonating( true );
            setImpersonatedUser( parsed.user );
            setImpersonatedByAdminId( parsed.impersonatedByAdminId ?? null );
          }
        } catch {
          // Corrupt payload — wipe so subsequent loads don't trip on it.
          try { sessionStorage.removeItem( IMPERSONATION_STORAGE_KEY ); } catch { /* private-browsing */ }
        }
      }
    } catch {
      // sessionStorage unavailable (private browsing, sandboxed iframe) —
      // proceed without restored state; the URL token branch below still
      // runs if the admin just opened the tab.
    }

    // TV1-1860 — strip `?impersonate=<token>` from the URL synchronously
    // BEFORE any await. The history.replaceState must happen before the
    // exchange await so a refresh mid-flight does not re-trigger the
    // exchange (and so React Router does not see the stale param).
    const url = new URL( window.location.href );
    const impersonateToken = url.searchParams.get( 'impersonate' );
    if ( impersonateToken !== null ) {
      url.searchParams.delete( 'impersonate' );
      const cleanedQuery = url.searchParams.toString();
      const cleanedPath = window.location.pathname + ( cleanedQuery ? `?${cleanedQuery}` : '' ) + window.location.hash;
      window.history.replaceState({}, '', cleanedPath );
    }

    const run = async () => {
      // TV1-1860 — token exchange path. The endpoint sets the BA session
      // cookie via a `Set-Cookie` header so subsequent requests on this
      // origin are authenticated as the impersonated user. On failure
      // (invalid/expired token) we silently fall through to the normal
      // auth path; the user will land on `/auth` if they have no session.
      if ( impersonateToken ) {
        try {
          const result = await backend.post.apply_impersonation_session({ token: impersonateToken });
          if ( result?.user ) {
            const adminId = result.impersonatedBy?.id ?? null;
            try {
              sessionStorage.setItem(
                IMPERSONATION_STORAGE_KEY,
                JSON.stringify({ user: result.user, impersonatedByAdminId: adminId })
              );
            } catch {
              // sessionStorage unavailable — React state still drives the
              // banner; we just lose the refresh-survival guarantee.
            }
            setIsImpersonating( true );
            setImpersonatedUser( result.user );
            setImpersonatedByAdminId( adminId );
            // Production-debug breadcrumb. The acting admin id is the
            // identifying signal for support tickets that originate from
            // an impersonated session.
            console.info( '[AUTH] Impersonation session applied', { user: result.user.email, adminId });
            Sentry.addBreadcrumb({ category: 'auth.impersonation', level: 'info', message: 'session applied', data: { user: result.user.email, adminId }});
          }
        } catch {
          // Invalid/expired token — quiet fallback to normal auth flow.
          clearImpersonationState();
        }
      }

      // --- Normal auth flow ---
      try {
        const profile = await backend.queries.UsersProfilesMe({});
        console.log( '[PERF] Authentication completed', {
          duration: `${( performance.now() - startTime ).toFixed( 0 )}ms`,
          user: profile?.email || 'unknown'
        });
        if ( !profile ) {
          // Session valid (the typed client would have rejected on 401
          // otherwise — see processFetchResponse in client-library SSE.ts)
          // but the UserProfileCreationPolicy projection hasn't landed yet.
          // KEEP isUserLoading=true so Protected HOC shows the loading state
          // instead of redirecting to /auth on `!user && !isUserLoading`.
          // The poll's success path (startProvisioningPoll) and the 30s
          // timeout both flip isUserLoading=false explicitly.
          console.warn( '[AUTH] Profile not yet projected — entering provisioning watch' );
          Sentry.addBreadcrumb({ category: 'auth.race', level: 'warning', message: 'profile not yet projected', data: { branch: 'null-profile' }});
          beginProvisioningWatch();
          return;
        }
        setUser( normalizeUser( profile as Partial<User> & Record<string, unknown> ));
        setIsUserLoading( false );

        // If this is a first-time landing (e.g. One Tap, or arrival on
        // /dashboard right after the OAuth callbackURL redirect), open the
        // provisioning watch so the user sees the overlay until the chain
        // completes.
        if ( !profile.accountId ) {
          console.info( '[AUTH] First-time landing — accountId pending' );
          Sentry.addBreadcrumb({ category: 'auth.race', level: 'info', message: 'first-time landing', data: { branch: 'no-accountid' }});
          beginProvisioningWatch();
        } else {
          subscribeToProfile();
          void hydrateCapabilities();
        }
      } catch ( error: any ) {
        console.error( '[AUTH] Authentication failed', {
          duration: `${( performance.now() - startTime ).toFixed( 0 )}ms`,
          error: error?.message || 'Unknown error'
        });
        if ( error?.status === 401 ) {
          // TV1-1860 — 401 means the BA session is gone (signed out elsewhere,
          // session expired, impersonation token revoked). Clear any cached
          // impersonation state so the next /auth landing does not show a
          // stale banner.
          clearImpersonationState();
          // No pushState here — Protected HOC handles the redirect declaratively;
          // imperative URL surgery races the magic-link consumer in pages/Authentication.
          setIsUserLoading( false );
        } else {
          // Any other error (network hiccup, 503 from the post-login
          // provisioning window — see featureFlags.readmodel.ts's
          // `ServiceUnavailableError` for accountId not yet bound) is
          // transient, not a real auth failure. Fall into the same bounded
          // watch the null-profile/no-accountId branches above use, instead
          // of leaving isUserLoading permanently stuck.
          console.warn( '[AUTH] Transient profile-fetch error — entering provisioning watch' );
          Sentry.addBreadcrumb({ category: 'auth.race', level: 'warning', message: 'transient profile fetch error', data: { branch: 'catch-non-401', error: error?.message }});
          beginProvisioningWatch();
        }
      }
    };

    run();

    return () => {
      profileSubscriptionRef.current?.close();
      profileSubscriptionRef.current = null;
      closeCapabilitiesSubscription();
      closeAccountSuspensionSignal();
      clearProvisioningTimeout();
      clearProvisioningPoll();
    };
  }, [backend]);

  return {
    user: effectiveUser,
    signin,
    signinWithPassword,
    loginWithGoogle,
    loginWithMercadoLibre,
    register,
    signout,
    validateMagicLink,
    requestPasswordReset,
    resetPassword,
    isLoadingUser,
    capabilitiesReady,
    isProvisioning,
    provisioningError,
    retryProvisioning,
    // TV1-1860 — admin impersonation surface area
    isImpersonating,
    impersonatedUser,
    impersonatedByAdminId,
    stopImpersonating
  };
}
